import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Card, 
  Row, 
  Col, 
  Typography, 
  Button, 
  Avatar, 
  Space, 
  Divider,
  Badge,
  Progress,
  List,
  Tag,
  Modal,
  Form,
  Input,
  Rate,
  Statistic,
  Alert,
  Drawer
} from 'antd';
import { 
  UserOutlined, 
  CalendarOutlined, 
  ToolOutlined, 
  BookOutlined,
  StarOutlined,
  EditOutlined,
  LogoutOutlined,
  BellOutlined,
  TrophyOutlined,
  CheckCircleOutlined,
  MenuOutlined
} from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { OnlineScheduling } from '../../features/services';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/config';

const { Header, Content, Sider } = Layout;
const { Title, Text, Paragraph } = Typography;

const UserDashboard = () => {
  const { currentUser, userProfile, logout, updateUserProfile } = useAuth();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
  const [serviceDetailsModalVisible, setServiceDetailsModalVisible] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [upcomingQuotes, setUpcomingQuotes] = useState([]);
  const [recentAppointments, setRecentAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  // Handle window resize for mobile responsiveness
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch appointment data when component loads
  useEffect(() => {
    const fetchAppointments = async () => {
      const emailOriginal = (userProfile?.email || currentUser?.email || '').trim();
      const userEmail = emailOriginal.toLowerCase();
      console.log('🧭 Dashboard email resolution:', { emailOriginal, userEmail, uid: currentUser?.uid });
      if (!emailOriginal) return;
      
      try {
        setLoading(true);
        console.log('📅 Fetching appointments for email:', userEmail);
        
        // Fetch appointments from Firestore with limit
        const appointmentsRef = collection(db, 'appointments');
        // Query by normalized field (no orderBy to avoid composite index requirement)
        const q1 = query(
          appointmentsRef,
          where('customerEmailLower', '==', userEmail)
        );
        // Fallback for older docs that only have customerEmail (no orderBy)
        const q2 = query(
          appointmentsRef,
          where('customerEmail', '==', emailOriginal)
        );

        const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);
        console.log('📥 Appointments query snapshots:', { q1Count: snap1.size, q2Count: snap2.size });
        const appointments = [];
        const seen = new Set();
        
        ;[snap1, snap2].forEach(snap => {
          snap.forEach((doc) => {
            if (seen.has(doc.id)) return;
            seen.add(doc.id);
            const data = doc.data();
            appointments.push({
              id: doc.id,
              ...data,
              createdAt: data.createdAt?.toDate?.() || new Date(),
              scheduledDate: data.scheduledDate || data.createdAt?.toDate?.()?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0]
            });
          });
        });

        // Also fetch paid/scheduled quotes for this user's email to show newly booked services
        // This covers cases where the appointment record hasn't been linked to the uid yet
        let quoteItems = [];
        if (userEmail) {
          try {
            const quotesRef = collection(db, 'quotes');
            const qq1 = query(quotesRef, where('customerEmailLower', '==', userEmail));
            const qq2 = query(quotesRef, where('customerEmail', '==', emailOriginal));
            const [qs1, qs2] = await Promise.all([getDocs(qq1), getDocs(qq2)]);
            console.log('📥 Quotes query snapshots:', {
              qq1Count: qs1.size,
              qq2Count: qs2.size
            });
            quoteItems = [];
            const seenQ = new Set();
            ;[qs1, qs2].forEach(qs => qs.forEach((qDoc) => {
              if (seenQ.has(qDoc.id)) return;
              seenQ.add(qDoc.id);
              const qd = qDoc.data();
              const status = qd.status || qd.paymentStatus || 'pending';
              // Only surface quotes that look booked/paid or scheduled
              const isUpcoming = status === 'scheduled' || status === 'confirmed' || status === 'paid' || status === 'succeeded';
              if (isUpcoming) {
                quoteItems.push({
                  id: qDoc.id,
                  quoteId: qDoc.id,
                  serviceNames: (() => {
                    try {
                      if (typeof qd.services === 'string') return JSON.parse(qd.services);
                      if (Array.isArray(qd.services)) return qd.services;
                    } catch {}
                    return qd.serviceNames || ['Service'];
                  })(),
                  status: status,
                  paymentStatus: qd.paymentStatus,
                  scheduledDate: new Date().toISOString().split('T')[0],
                  scheduledTime: ''
                });
              }
            }));
          } catch (e) {
            console.error('Error fetching quotes for dashboard:', e);
          }
        }
        
        // Sort appointments by scheduled date (most recent first)
        appointments.sort((a, b) => {
          if (a.scheduledDate < b.scheduledDate) return 1;
          if (a.scheduledDate > b.scheduledDate) return -1;
          return 0;
        });
        
        // Filter upcoming and recent appointments
        const today = new Date().toISOString().split('T')[0];
        const upcoming = appointments.filter(apt => 
          apt.scheduledDate >= today && 
          (apt.status === 'scheduled' || apt.status === 'confirmed') &&
          apt.status !== 'cancelled'
        );
        const recent = appointments.filter(apt => 
          apt.scheduledDate < today || 
          apt.status === 'completed' || 
          apt.status === 'cancelled'
        ).slice(0, 5);

        // Prepare separate list for upcoming quotes (do not merge with appointments)
        const upcomingQuotesOnly = quoteItems;
        
        console.log('📊 Found appointments:', {
          total: appointments.length,
          upcomingAppointments: upcoming.length,
          upcomingQuotes: upcomingQuotesOnly.length,
          recent: recent.length
        });
        
        setUpcomingAppointments(upcoming);
        setUpcomingQuotes(upcomingQuotesOnly);
        setRecentAppointments(recent);
      } catch (error) {
        console.error('Error fetching appointments:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, [currentUser?.uid]);

  // Function to refresh appointments (called when modal closes after scheduling)
  const refreshAppointments = async () => {
    if (!currentUser?.uid) return;
    
    try {
      console.log('🔄 Refreshing appointments...');
      
      // Fetch appointments from Firestore
      const appointmentsRef = collection(db, 'appointments');
      const userQuery = query(
        appointmentsRef, 
        where('customerId', '==', currentUser.uid)
      );
      
      const snapshot = await getDocs(userQuery);
      const appointments = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        appointments.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          scheduledDate: data.scheduledDate || data.createdAt?.toDate?.()?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0]
        });
      });
      
      // Sort appointments by scheduled date (most recent first)
      appointments.sort((a, b) => {
        if (a.scheduledDate < b.scheduledDate) return 1;
        if (a.scheduledDate > b.scheduledDate) return -1;
        return 0;
      });
      
      // Filter upcoming and recent appointments
      const today = new Date().toISOString().split('T')[0];
      const upcoming = appointments.filter(apt => 
        apt.scheduledDate >= today && 
        (apt.status === 'scheduled' || apt.status === 'confirmed') &&
        apt.status !== 'cancelled'
      );
      const recent = appointments.filter(apt => 
        apt.scheduledDate < today || 
        apt.status === 'completed' || 
        apt.status === 'cancelled'
      ).slice(0, 5);
      
      setUpcomingAppointments(upcoming);
      setRecentAppointments(recent);
      console.log('✅ Appointments refreshed:', {
        total: appointments.length,
        upcoming: upcoming.length,
        recent: recent.length
      });
    } catch (error) {
      console.error('Error refreshing appointments:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      // Navigate to home even if logout fails
      navigate('/');
    }
  };

  const handleServiceClick = (service) => {
    // Normalize serviceTypes for the editor based on known names
    const nameArray = Array.isArray(service.serviceNames)
      ? service.serviceNames
      : typeof service.serviceNames === 'string'
        ? service.serviceNames.split(',').map(s => s.trim())
        : [];

    const nameToId = {
      'Gutter Cleaning': 'gutter-cleaning',
      'Gutter cleaning': 'gutter-cleaning',
      'gutter cleaning': 'gutter-cleaning',
      'Power Washing': 'cleaning',
      'Power Wash': 'cleaning',
      'power wash': 'cleaning',
      'Deep Cleaning': 'cleaning',
      'Lawn Care': 'lawn-care',
      'Painting': 'painting',
      'General Maintenance': 'maintenance',
      'Landscaping': 'landscaping'
    };

    const inferredTypes = nameArray
      .map(n => nameToId[n] || nameToId[n?.toLowerCase?.()] )
      .filter(Boolean);

    const normalized = {
      ...service,
      serviceTypes: service.serviceTypes || (inferredTypes.length ? inferredTypes : undefined),
      serviceType: service.serviceType || (inferredTypes.length ? inferredTypes[0] : undefined)
    };

    setSelectedService(normalized);
    setServiceDetailsModalVisible(true);
  };


  const handleUpdateProfile = async (values) => {
    try {
      await updateUserProfile(currentUser.uid, values);
      setEditModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Update error:', error);
    }
  };

  // Use real appointment data from Firestore
  const upcomingServices = upcomingAppointments;
  const recentServices = recentAppointments;
  const workshops = userProfile?.workshops || [];

  // Debug logging
  console.log('🔍 Dashboard Debug Info:');
  console.log('- Current User:', currentUser?.uid);
  console.log('- User Profile:', userProfile);
  console.log('- User Profile createdAt:', userProfile?.createdAt);
  console.log('- Loading:', loading);
  console.log('- Upcoming Appointments:', upcomingAppointments);
  console.log('- Upcoming Services (for display):', upcomingServices);
  console.log('- Recent Appointments:', recentAppointments);

  // Sidebar content component
  const SidebarContent = () => (
    <div style={{ padding: '24px' }}>
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <Avatar 
          size={80} 
          icon={<UserOutlined />} 
          style={{ marginBottom: '1rem' }}
        />
        <Title level={4} style={{ margin: 0 }}>
          {userProfile?.firstName} {userProfile?.lastName}
        </Title>
        <Text type="secondary">{userProfile?.email}</Text>
        <br />
        <Tag color="blue" style={{ marginTop: '0.5rem' }}>
          {userProfile?.membershipPlan?.toUpperCase()} Member
        </Tag>
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <Button 
          type="primary" 
          icon={<EditOutlined />}
          block
          onClick={() => {
            setEditModalVisible(true);
            setMobileMenuVisible(false);
          }}
        >
          Edit Profile
        </Button>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <Button 
          type="default" 
          icon={<CalendarOutlined />}
          block
          onClick={() => {
            setScheduleModalVisible(true);
            setMobileMenuVisible(false);
          }}
          style={{
            borderColor: '#1890ff',
            color: '#1890ff'
          }}
        >
          Schedule Service
        </Button>
      </div>

      <Button 
        type="text" 
        icon={<LogoutOutlined />}
        onClick={handleLogout}
        style={{ color: '#ff4d4f' }}
      >
        Sign Out
      </Button>
    </div>
  );

  return (
    <Layout style={{ minHeight: '100vh', marginTop: '64px' }}>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sider 
          width={300} 
          style={{ 
            background: '#fff'
          }}
        >
          <SidebarContent />
        </Sider>
      )}

      <Layout>
        {/* Mobile Header */}
        {isMobile && (
          <div style={{ 
            padding: '16px',
            background: '#fff',
            borderBottom: '1px solid #f0f0f0'
          }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Space>
                <Avatar 
                  size={40} 
                  icon={<UserOutlined />} 
                />
                <div>
                  <Title level={5} style={{ margin: 0 }}>
                    {userProfile?.firstName} {userProfile?.lastName}
                  </Title>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {userProfile?.membershipPlan?.toUpperCase()} Member
                  </Text>
                </div>
              </Space>
            </Col>
            <Col>
              <Button 
                type="text" 
                icon={<MenuOutlined />}
                onClick={() => setMobileMenuVisible(true)}
              />
            </Col>
          </Row>
          </div>
        )}

        <Content style={{ padding: isMobile ? '16px' : '24px' }}>
          {/* Dashboard Stats */}
          <Row gutter={[16, 16]} style={{ marginBottom: '2rem' }}>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Upcoming Services"
                  value={upcomingServices.length}
                  prefix={<CalendarOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Completed Services"
                  value={recentServices.length}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Member Since"
                  value={(() => {
                    try {
                      if (userProfile?.createdAt) {
                        const date = new Date(userProfile.createdAt);
                        return isNaN(date.getTime()) ? '2024' : date.getFullYear();
                      }
                      return '2024';
                    } catch (error) {
                      console.error('Error parsing createdAt:', error);
                      return '2024';
                    }
                  })()}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={[24, 24]}>
            {/* Upcoming Appointments (linked to your account) */}
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <CalendarOutlined />
                    Upcoming Appointments
                    {loading && <span style={{ fontSize: '12px', color: '#999' }}>(Loading...)</span>}
                  </Space>
                }
                extra={
                  <Space>
                    <Badge count={upcomingServices.length} />
                    {upcomingServices.length > 0 && (
                      <Button
                        type="primary"
                        shape="circle"
                        icon={<CalendarOutlined />}
                        size="small"
                        onClick={() => setScheduleModalVisible(true)}
                        style={{
                          background: 'linear-gradient(135deg, #FD865C, #ff9a6b)',
                          border: 'none',
                          boxShadow: '0 2px 4px rgba(253, 134, 92, 0.3)'
                        }}
                        title="Schedule Another Service"
                      />
                    )}
                  </Space>
                }
              >
                {upcomingServices.length > 0 ? (
                  <List
                    dataSource={upcomingServices}
                    renderItem={item => (
                      <List.Item
                        style={{ 
                          cursor: 'pointer',
                          padding: '12px',
                          borderRadius: '8px',
                          transition: 'background-color 0.2s'
                        }}
                        onClick={() => handleServiceClick(item)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f5f5f5';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <List.Item.Meta
                          title={item.serviceNames || item.serviceType || 'Service'}
                          description={`${item.scheduledDate} at ${item.scheduledTime}`}
                        />
                        <Tag color={item.status === 'scheduled' ? 'blue' : item.status === 'confirmed' ? 'green' : 'orange'}>
                          {item.status}
                        </Tag>
                      </List.Item>
                    )}
                  />
                ) : (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
                    <CalendarOutlined style={{ fontSize: '2rem', marginBottom: '1rem' }} />
                    <br />
                    <Text type="secondary" style={{ display: 'block', marginBottom: '1rem' }}>
                      {loading ? 'Loading appointments...' : 'No upcoming services scheduled'}
                    </Text>
                    <Button 
                      type="primary" 
                      icon={<CalendarOutlined />}
                      onClick={() => setScheduleModalVisible(true)}
                      style={{
                        background: 'linear-gradient(135deg, #1890ff, #40a9ff)',
                        border: 'none',
                        borderRadius: '8px'
                      }}
                    >
                      Schedule a Service
                    </Button>
                  </div>
                )}
              </Card>
            </Col>

            {/* Service Quotes (recently booked) */}
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <ToolOutlined />
                    Service Quotes
                  </Space>
                }
              >
                {upcomingQuotes.length > 0 ? (
                  <List
                    dataSource={upcomingQuotes}
                    renderItem={item => (
                      <List.Item>
                        <List.Item.Meta
                          title={(item.serviceNames && item.serviceNames.join(', ')) || 'Service'}
                          description={`Booking ID: ${item.quoteId || item.id}`}
                        />
                        <Tag color={item.status === 'scheduled' ? 'blue' : item.status === 'confirmed' ? 'green' : 'gold'}>
                          {item.status}
                        </Tag>
                      </List.Item>
                    )}
                  />
                ) : (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
                    <ToolOutlined style={{ fontSize: '2rem', marginBottom: '1rem' }} />
                    <br />
                    <Text type="secondary">No upcoming quotes found</Text>
                  </div>
                )}
              </Card>
            </Col>

            {/* Recent Services */}
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <ToolOutlined />
                    Recent Services
                  </Space>
                }
              >
                {recentServices.length > 0 ? (
                  <List
                    dataSource={recentServices}
                    renderItem={item => (
                      <List.Item>
                        <List.Item.Meta
                          title={item.serviceNames || item.serviceType || 'Service'}
                          description={`${item.scheduledDate} at ${item.scheduledTime}`}
                        />
                        <Space>
                          <Rate disabled defaultValue={item.rating || 0} />
                          <Tag color={item.status === 'completed' ? 'green' : item.status === 'cancelled' ? 'red' : 'orange'}>
                            {item.status}
                          </Tag>
                        </Space>
                      </List.Item>
                    )}
                  />
                ) : (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
                    <ToolOutlined style={{ fontSize: '2rem', marginBottom: '1rem' }} />
                    <br />
                    <Text type="secondary">No recent services</Text>
                  </div>
                )}
              </Card>
            </Col>

            
          </Row>

          {/* Help Section */}
          <Card style={{ marginTop: '2rem' }}>
            <Alert
              message="Need Help?"
              description={
                <div>
                  <Paragraph style={{ margin: 0 }}>
                    Our customer support team is here to help you with any questions about your services.
                  </Paragraph>
                  <Space style={{ marginTop: '0.5rem' }}>
                    <Button type="link" style={{ padding: 0 }}>
                      Contact Support
                    </Button>
                    <Button type="link" style={{ padding: 0 }}>
                      View FAQ
                    </Button>
                    <Button type="link" style={{ padding: 0 }}>
                      Service Guidelines
                    </Button>
                  </Space>
                </div>
              }
              type="info"
              showIcon
            />
          </Card>
        </Content>
      </Layout>

      {/* Edit Profile Modal */}
      <Modal
        title="Edit Profile"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdateProfile}
          initialValues={userProfile}
        >
          <Form.Item
            name="firstName"
            label="First Name"
            rules={[{ required: true, message: 'Please enter your first name' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="lastName"
            label="Last Name"
            rules={[{ required: true, message: 'Please enter your last name' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Phone Number"
            rules={[{ required: true, message: 'Please enter your phone number' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="address"
            label="Address"
            rules={[{ required: true, message: 'Please enter your address' }]}
          >
            <Input />
          </Form.Item>

          <div style={{ textAlign: 'center' }}>
            <Space>
              <Button onClick={() => setEditModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Update Profile
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Schedule Service Modal */}
      <Modal
        title={null}
        open={scheduleModalVisible}
        onCancel={() => setScheduleModalVisible(false)}
        footer={null}
        width={isMobile ? '100%' : 900}
        style={{ top: 20, paddingBottom: 0 }}
        zIndex={10000}
        destroyOnClose={true}
        closable={true}
        styles={{
          body: {
            padding: 0,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            borderRadius: 12,
            maxHeight: 'calc(100vh - 160px)',
            overflowY: 'auto'
          },
          mask: {
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)'
          }
        }}
        className="custom-schedule-modal"
      >
        <div style={{
          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
          borderRadius: '12px',
          overflow: 'hidden'
        }}>
          <OnlineScheduling onSuccess={() => {
          setScheduleModalVisible(false);
          refreshAppointments();
        }} />
        </div>
      </Modal>

      {/* Service Details Modal */}
      <Modal
        title={null}
        open={serviceDetailsModalVisible}
        onCancel={() => {
          setServiceDetailsModalVisible(false);
          setSelectedService(null);
        }}
        footer={null}
        width={isMobile ? '100%' : 900}
        style={{ top: 20, paddingBottom: 0 }}
        zIndex={10000}
        destroyOnClose={true}
        closable={true}
        styles={{
          body: {
            padding: 0,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            borderRadius: 12,
            maxHeight: 'calc(100vh - 160px)',
            overflowY: 'auto'
          },
          mask: {
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)'
          }
        }}
        className="custom-schedule-modal"
      >
        <div style={{
          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
          borderRadius: '12px',
          overflow: 'hidden'
        }}>
          <OnlineScheduling 
            onSuccess={() => {
              setServiceDetailsModalVisible(false);
              setSelectedService(null);
              refreshAppointments();
            }}
            editMode={true}
            existingAppointment={selectedService}
          />
        </div>
      </Modal>

      {/* Mobile Drawer */}
      <Drawer
        title="Dashboard Menu"
        placement="left"
        onClose={() => setMobileMenuVisible(false)}
        open={mobileMenuVisible}
        width={280}
        bodyStyle={{ padding: 0 }}
      >
        <SidebarContent />
      </Drawer>
    </Layout>
  );
};

export default UserDashboard;
